import { CourseStatsResponse, ConversationStatsResponse } from '@/types/Stats'
import { refreshAuthTokenAndUpdateUser } from '@/auth/utils'

const API_URL = import.meta.env.VITE_API_URL

/**
 * Fetches statistics for all courses.
 */
export const getCourseStats = async (signal: AbortSignal): Promise<CourseStatsResponse | undefined> => {
  try {
    const idToken = await refreshAuthTokenAndUpdateUser()

    const response = await fetch(`${API_URL}/api/stats/courses`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
      signal,
    })

    if (response.status === 204) {
      throw new Error('No courses found')
    }

    if (!response.ok) {
      const body = await response.json()
      throw new Error(body.message)
    }

    return await response.json()
  } catch (error) {
    if (signal.aborted) {
      return
    }
    if (error instanceof Error) {
      throw error
    }
    throw new Error('An error occurred while fetching course statistics.')
  }
}

/**
 * Fetches conversation statistics for a specific period.
 */
export const getConversationStats = async (
  startDate: string,
  endDate: string,
  courseName?: string,
  signal?: AbortSignal,
): Promise<ConversationStatsResponse | undefined> => {
  const queryParams = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    ...(courseName && { course_name: courseName }),
  })

  try {
    const idToken = await refreshAuthTokenAndUpdateUser()

    const response = await fetch(`${API_URL}/api/stats/conversations?${queryParams}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
      signal,
    })

    if (response.status === 204) {
      throw new Error('No conversations found in the specified period')
    }

    if (!response.ok) {
      const body = await response.json()
      throw new Error(body.message)
    }

    return await response.json()
  } catch (error) {
    if (signal?.aborted) {
      return
    }
    if (error instanceof Error) {
      throw error
    }
    throw new Error('An error occurred while fetching conversation statistics.')
  }
}
